import { useEffect, useMemo, useRef, useState } from 'react';
import {
	CustomAction,
	CustomActionHandlerContext,
	Definition,
	DefinitionWalker,
	ObjectCloner,
	Sequence,
	Step,
	ToolboxConfiguration,
	Uid
} from 'sequential-workflow-designer';
import {
	ClickablePlaceholderClickedCustomAction,
	ClickablePlaceholderProExtension,
	IconBadgeClickedCustomAction,
	IconBadgeProExtension
} from '@nocode-js/sequential-workflow-designer-pro';
import {
	SequentialWorkflowDesigner,
	useSequentialWorkflowDesignerController,
	wrapDefinition,
	WrappedDefinition
} from 'sequential-workflow-designer-react';
import './CopySteps.css';

function createTaskStep(name: string): Step {
	return {
		id: Uid.next(),
		componentType: 'task',
		type: 'task',
		name,
		properties: {}
	};
}

const sourceToolboxConfiguration: ToolboxConfiguration = {
	groups: [
		{
			name: 'Steps',
			steps: [createTaskStep('Gray'), createTaskStep('Green'), createTaskStep('Yellow')]
		}
	]
};

const startSourceDefinition: Definition = {
	properties: {},
	sequence: [createTaskStep('Black'), createTaskStep('Red'), createTaskStep('Ping'), createTaskStep('Blue')]
};

const startTargetDefinition: Definition = {
	properties: {},
	sequence: []
};

export function CopySteps() {
	const [sourceDefinition, setSourceDefinition] = useState(() => wrapDefinition(startSourceDefinition));
	const [targetDefinition, setTargetDefinition] = useState(() => wrapDefinition(startTargetDefinition));
	const [resetSelection, setResetSelection] = useState(0);
	const selectedSteps = useRef<string[]>([]);

	function getSelectedStepsAndResetSelection(): Step[] {
		setResetSelection(s => s + 1);
		const steps: Step[] = [];
		const walker = new DefinitionWalker();
		for (const id of selectedSteps.current) {
			const sourceStep = walker.getById(sourceDefinition.value, id);
			if (sourceStep) {
				const targetStep = ObjectCloner.deepClone(sourceStep);
				targetStep.id = Uid.next();
				walker.forEachChildren(targetStep, step => {
					step.id = Uid.next();
				});
				steps.push(targetStep);
			}
		}
		return steps;
	}

	return (
		<div className="page" data-testid="copy-steps">
			<h2>Copy Steps Between Workflows</h2>

			<div className="copy-side">
				<h3>Source Workflow</h3>
				<p>Select the steps you want to copy.</p>
				<SourceDesigner
					definition={sourceDefinition}
					resetSelection={resetSelection}
					setDefinition={setSourceDefinition}
					onSelectionChanged={ids => (selectedSteps.current = ids)}
				/>
			</div>
			<div className="copy-side">
				<h3>Target Workflow</h3>
				<p>Select where you want to copy the selected steps.</p>
				<TargetDesigner
					definition={targetDefinition}
					setDefinition={setTargetDefinition}
					getSelectedStepsAndResetSelection={getSelectedStepsAndResetSelection}
				/>
			</div>
		</div>
	);
}

function SourceDesigner(props: {
	definition: WrappedDefinition;
	resetSelection: number;
	setDefinition: (definition: WrappedDefinition) => void;
	onSelectionChanged: (stepIds: string[]) => void;
}) {
	const controller = useSequentialWorkflowDesignerController();
	const selectedSteps = useRef<Record<string, boolean>>({});

	const extensions = useMemo(
		() => [
			IconBadgeProExtension.create({
				iconProvider: step => {
					const isSelected = selectedSteps.current[step.id];
					return {
						d: isSelected ? 'M19.206 40.273 5.727 26.793l3.37-3.37 10.11 10.11 21.696-21.697 3.37 3.37z' : '',
						type: isSelected ? 'selected' : 'not-selected',
						isClickable: true
					};
				}
			})
		],
		[]
	);

	function customActionHandler(action: CustomAction, step: Step | null) {
		if (step && action.type === 'iconBadgeClicked') {
			const ibc = action as IconBadgeClickedCustomAction;
			if (ibc.badgeType === 'selected' || ibc.badgeType === 'not-selected') {
				selectedSteps.current[step.id] = !selectedSteps.current[step.id];
				controller.updateBadges();

				const ids = Object.keys(selectedSteps.current).filter(id => selectedSteps.current[id]);
				props.onSelectionChanged(ids);
			}
		}
	}

	useEffect(() => {
		if (props.resetSelection !== 0) {
			selectedSteps.current = {};
			controller.updateBadges();
			props.onSelectionChanged([]);
		}
	}, [props, controller]);

	return (
		<SequentialWorkflowDesigner
			undoStackSize={10}
			definition={props.definition}
			onDefinitionChange={props.setDefinition}
			toolboxConfiguration={sourceToolboxConfiguration}
			stepsConfiguration={{}}
			controlBar={false}
			rootEditor={false}
			stepEditor={false}
			extensions={extensions}
			customActionHandler={customActionHandler}
			controller={controller}
		/>
	);
}

function TargetDesigner(props: {
	definition: WrappedDefinition;
	setDefinition: (definition: WrappedDefinition) => void;
	getSelectedStepsAndResetSelection: () => Step[];
}) {
	const extensions = useMemo(() => [ClickablePlaceholderProExtension.create()], []);

	function customActionHandler(action: CustomAction, _: Step | null, sequence: Sequence, context: CustomActionHandlerContext) {
		if (action.type === 'clickablePlaceholderClicked') {
			const cpc = action as ClickablePlaceholderClickedCustomAction;
			const steps = props.getSelectedStepsAndResetSelection();
			if (steps.length === 0) {
				return;
			}
			sequence.splice(cpc.index, 0, ...steps);
			context.notifyStepInserted(steps[0].id);
		}
	}

	return (
		<SequentialWorkflowDesigner
			undoStackSize={10}
			definition={props.definition}
			onDefinitionChange={props.setDefinition}
			toolboxConfiguration={false}
			stepsConfiguration={{}}
			controlBar={false}
			rootEditor={false}
			stepEditor={false}
			extensions={extensions}
			customActionHandler={customActionHandler}
		/>
	);
}
